/**
 * The conspicuous local-only role banner (#567).
 *
 * Always visible while the simulation lane is active — above the shell, above
 * every fail-closed screen — because the entire point is that a developer who
 * switched themselves to `no-access` five minutes ago cannot mistake the
 * resulting 403s for a broken deployment: the amber strip names the simulated
 * profile and offers the one-click way back to Admin.
 *
 * The roles shown are the CONTROLLER's answer (the live local assignment
 * rows), not a browser belief: every change round-trips through the
 * controller and then refetches the session, so what this strip claims and
 * what the Editor enforces converge on the next request.
 */

import { useId, useState } from "react";

import { Button } from "@/ui/Button";
import { Select } from "@/ui/Input";

import { useLocalDev } from "./LocalDevProvider";
import { LOCAL_ROLES, NAMED_PROFILES, type LocalRole } from "./localAccess";

const CUSTOM = "custom";

function profileValue(roles: LocalRole[] | undefined): string {
  if (roles === undefined) return CUSTOM;
  const named = NAMED_PROFILES.find(
    (profile) =>
      profile.roles.length === roles.length && profile.roles.every((role) => roles.includes(role)),
  );
  return named ? named.label : CUSTOM;
}

export function LocalDevBanner() {
  const localDev = useLocalDev();
  const selectId = useId();
  const [customOpen, setCustomOpen] = useState(false);
  const [customRoles, setCustomRoles] = useState<LocalRole[]>([]);

  if (!localDev) return null;
  const { profile, error, applying, apply } = localDev;

  const roles = profile?.roles;
  const selected = customOpen ? CUSTOM : profileValue(roles);
  const effective =
    roles === undefined ? "unknown" : roles.length === 0 ? "no access" : roles.join(", ");
  const isAdmin = roles !== undefined && roles.length === 1 && roles[0] === "admin";

  const applyRoles = (next: LocalRole[]) => {
    setCustomOpen(false);
    void apply(next).catch(() => {
      // The provider recorded the failure; the strip renders it below.
    });
  };

  return (
    <aside
      aria-label="Local development role simulation"
      // sticky + z-50: the shell's sidebar is `fixed … z-40` and ignores flow,
      // so without an explicit stacking order it paints OVER the strip's left
      // edge — hiding exactly the "Simulated role:" text the banner exists to
      // keep conspicuous (observed live on the k8s lane).
      className="sticky top-0 z-50 border-b-2 border-amber-500 bg-amber-100 px-4 py-2 text-amber-950"
    >
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-semibold uppercase tracking-wide">Local dev</span>
        <span>
          Simulated role{roles !== undefined && roles.length === 1 ? "" : "s"}:{" "}
          <strong data-testid="local-dev-effective-roles">{effective}</strong>
        </span>
        <label htmlFor={selectId} className="sr-only">
          Switch your local role profile
        </label>
        <Select
          id={selectId}
          value={selected}
          aria-disabled={applying || undefined}
          onChange={(event) => {
            const choice = event.target.value;
            if (choice === CUSTOM) {
              setCustomRoles(roles ?? []);
              setCustomOpen(true);
              return;
            }
            const named = NAMED_PROFILES.find((profile) => profile.label === choice);
            if (named && !applying) applyRoles([...named.roles]);
          }}
          className="h-8 w-auto min-w-44"
        >
          {NAMED_PROFILES.map((profile) => (
            <option key={profile.label} value={profile.label}>
              {profile.label}
            </option>
          ))}
          <option value={CUSTOM}>Combination…</option>
        </Select>
        {!isAdmin ? (
          <Button
            size="sm"
            variant="outline"
            disabled={applying}
            onClick={() => applyRoles(["admin"])}
          >
            Restore Admin
          </Button>
        ) : null}
        {applying ? <span role="status">Applying…</span> : null}
        {error ? (
          <span role="alert" className="font-medium text-red-800">
            Role controls unavailable — the lane is behaving strictly. {error}
          </span>
        ) : null}
      </div>
      {customOpen ? (
        <form
          className="mt-2 flex flex-wrap items-center gap-3 text-sm"
          onSubmit={(event) => {
            event.preventDefault();
            if (!applying) applyRoles([...customRoles]);
          }}
        >
          {LOCAL_ROLES.map((role) => (
            <label key={role} className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={customRoles.includes(role)}
                onChange={(event) => {
                  setCustomRoles((current) =>
                    event.target.checked
                      ? [...current, role]
                      : current.filter((existing) => existing !== role),
                  );
                }}
              />
              {role}
            </label>
          ))}
          <Button size="sm" type="submit" disabled={applying}>
            Apply combination
          </Button>
          <Button size="sm" variant="ghost" type="button" onClick={() => setCustomOpen(false)}>
            Cancel
          </Button>
        </form>
      ) : null}
    </aside>
  );
}
